import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Card, Col, Spinner } from 'react-bootstrap';
import Logo from '../../images/SKIP.svg';
import axiosInstance from '../../api/api';
import { getUserFacingError } from '../../utils/userFacingError';
import { API_ROUTES } from '../../config/api-routes';
import { validationMessages } from '../../config/validationMessages';
import { passwordRegex } from '../../config/regex';
import './resetPass.css';

const {
  PASSWORDS_DO_NOT_MATCH,
  ERROR_DURING_PASSWORD_RESET,
  PASSWORD_MUST_CONTAIN_8_CHARACTERS,
  PASSWORD_RESET_FAILED,
} = validationMessages;

const ResetPassword = () => {
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleResetPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage('');

    if (!token?.trim()) {
      setMessage('This reset link is invalid. Request a new password reset email.');
      return;
    }

    if (password !== confirmPassword) {
      setMessage(PASSWORDS_DO_NOT_MATCH);
      return;
    }

    if (!passwordRegex.test(password)) {
      setMessage(PASSWORD_MUST_CONTAIN_8_CHARACTERS);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axiosInstance.post(`${API_ROUTES.AUTH.RESET_PASSWORD}/${token}`, {
        password,
        token,
      });
      const data = response.data as {
        success?: boolean;
        message?: string;
        detail?: string;
      };
      if (data.success !== false) {
        setSuccessMessage(true);
      } else {
        setMessage(`${PASSWORD_RESET_FAILED} ${data.message || data.detail || ''}`.trim());
      }
    } catch (error: unknown) {
      setMessage(getUserFacingError(error, ERROR_DURING_PASSWORD_RESET));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="reset-main-wrapper">
      <Col sm={12} className="justify-content-center">
        <img
          className="mb-3"
          alt="SkipScreen"
          src={Logo}
          width={150}
          style={{ display: 'block', margin: 'auto' }}
        />
        {successMessage ? (
          <Card className="p-5 success-wrapper">
            <h4 className="card-title text-center mb-4">Reset Password</h4>
            <Card.Text style={{ textAlign: 'center' }}>
              Your password has been successfully reset. Please{' '}
              <Link to="/" className="reset-login-link">
                log in
              </Link>{' '}
              with your new password.
            </Card.Text>
          </Card>
        ) : (
          <Card className="justify-content-center p-5 reset-form">
            <h4 className="card-title text-center mb-4">Reset Password</h4>
            <form onSubmit={handleResetPassword} noValidate>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirm-password">Confirm password</label>
                <input
                  type="password"
                  id="confirm-password"
                  className="form-control"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  required
                />
              </div>
              <Button
                type="submit"
                variant="success"
                className="submit-btn-reset"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="reset-submit-inner">
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden />
                    Resetting password…
                  </span>
                ) : (
                  'Reset Password'
                )}
              </Button>
              {message ? (
                <div className="alert alert-danger mt-3" role="alert">
                  {message}
                </div>
              ) : null}
            </form>
          </Card>
        )}
      </Col>
    </div>
  );
};

export default ResetPassword;
